import React, { useState, useEffect } from "react";
import { apiEndpoints, adminFetch } from "../config/api";
import {
  FiTrash2,
  FiEye,
  FiRefreshCw,
  FiX,
  FiCheckCircle,
} from "react-icons/fi";
import { format } from "date-fns";
import ReviewModal from "./ReviewModal";

const BookingsManager = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewBooking, setViewBooking] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewBooking, setReviewBooking] = useState(null);

  const statusOptions = [
    {
      value: "pending",
      label: "Pending",
      className: "ui-mdc-booking-status-badge--pending",
    },
    {
      value: "confirmed",
      label: "Confirmed",
      className: "ui-mdc-booking-status-badge--confirmed",
    },
    {
      value: "cancelled",
      label: "Cancelled",
      className: "ui-mdc-booking-status-badge--cancelled",
    },
    {
      value: "completed",
      label: "Completed",
      className: "ui-mdc-booking-status-badge--completed",
    },
  ];

  const paymentStatusOptions = [
    {
      value: "pending",
      label: "Pending",
      className: "ui-mdc-booking-payment-badge--pending",
    },
    {
      value: "paid",
      label: "Paid",
      className: "ui-mdc-booking-payment-badge--paid",
    },
    {
      value: "failed",
      label: "Failed",
      className: "ui-mdc-booking-payment-badge--failed",
    },
    {
      value: "refunded",
      label: "Refunded",
      className: "ui-mdc-booking-payment-badge--refunded",
    },
  ];

  // Fetch all bookings
  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('📡 Fetching bookings from:', apiEndpoints.bookings.getAll);
      const response = await adminFetch(apiEndpoints.bookings.getAll);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch bookings: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📦 Received bookings data:', data);
      
      if (data.success && data.bookings) {
        setBookings(data.bookings);
      } else {
        setBookings([]);
        setError(data.message || "No bookings found");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching bookings:", err);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  // Delete booking
  const deleteBooking = async (id) => {
    try {
      const response = await adminFetch(apiEndpoints.bookings.delete(id), {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete booking");
      }
      return await response.json();
    } catch (err) {
      throw err;
    }
  };

  // Load bookings on component mount
  useEffect(() => {
    fetchBookings();
  }, []);

  const handleView = (booking) => {
    setViewBooking(booking);
    setIsViewModalOpen(true);
  };

  const handleDelete = (id) => {
    setBookingToDelete(id);
    setIsDeleteConfirmOpen(true);
  };

  const handleAddReview = (booking) => {
    setReviewBooking(booking);
    setIsReviewModalOpen(true);
  };

  const handleConfirmBooking = async (id) => {
    if (!window.confirm("Are you sure you want to confirm this booking ?")) {
      return;
    }

    try {
      const response = await adminFetch(apiEndpoints.bookings.update(id), {
        method: "PUT",
        body: JSON.stringify({
          status: "confirmed",
          paymentStatus: "paid",
          adminNotes: "Confirmed with customer",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to confirm booking");
      }

      const data = await response.json();
      if (data.success) {
        // Refresh bookings to show updated status
        await fetchBookings();
      } else {
        throw new Error(data.message || "Failed to confirm booking");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error confirming booking:", err);
      alert(err.message || "Failed to confirm booking. Please try again.");
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteBooking(bookingToDelete);
      await fetchBookings();
      setIsDeleteConfirmOpen(false);
      setBookingToDelete(null);
    } catch (err) {
      setError(err.message);
      console.error("Error deleting booking:", err);
    }
  };

  const getStatusBadge = (status) => {
    if (!status || status === "") {
      const statusOption = statusOptions.find((s) => s.value === "pending");
      return (
        <span className={`ui-mdc-booking-status-badge ${statusOption?.className || ""}`}>
          {statusOption?.label || "Pending"}
        </span>
      );
    }
    const statusOption = statusOptions.find((s) => s.value === status);
    return (
      <span className={`ui-mdc-booking-status-badge ${statusOption?.className || ""}`}>
        {statusOption?.label || status}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    if (!paymentStatus || paymentStatus === "") {
      const statusOption = paymentStatusOptions.find((s) => s.value === "pending");
      return (
        <span className={`ui-mdc-booking-payment-badge ${statusOption?.className || ""}`}>
          {statusOption?.label || "Pending"}
        </span>
      );
    }
    const statusOption = paymentStatusOptions.find((s) => s.value === paymentStatus);
    return (
      <span className={`ui-mdc-booking-payment-badge ${statusOption?.className || ""}`}>
        {statusOption?.label || paymentStatus}
      </span>
    );
  };

  const formatTimeSlot = (timeSlot) => {
    const slotMap = {
      morning: "Morning",
      afternoon: "Afternoon",
      extended: "Extended",
      fullDay: "Full Day",
    };
    return slotMap[timeSlot] || timeSlot || "N/A";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="ui-mdc-package-loading">
        <div className="ui-mdc-package-loading-spinner"></div>
        <span>Loading bookings...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header Section */}
      <div className="ui-mdc-package-header">
        <h1 className="ui-mdc-package-title">Booking Management</h1>
        <button
          onClick={fetchBookings}
          className="ui-mdc-package-button ui-mdc-package-button--secondary mt-5"
          disabled={loading}
        >
          <FiRefreshCw className={loading ? "animate-spin" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="ui-mdc-booking-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Bookings Table */}
      {!loading && (
        <div className="ui-mdc-booking-table-wrapper">
          <div className="ui-mdc-booking-table-container">
            <table className="ui-mdc-booking-table">
              <thead className="ui-mdc-booking-table-head">
                <tr>
                  <th>Booking ID</th>
                  <th>Customer</th>
                  <th>Date & Time</th>
                  <th>Package</th>
                  <th>People</th>
                  <th>Total Price</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="ui-mdc-booking-table-body">
                {bookings.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="ui-mdc-booking-table-empty">
                      No bookings found.
                    </td>
                  </tr>
                ) : (
                  bookings.map((booking) => (
                    <tr key={booking._id} className="ui-mdc-booking-table-row">
                      <td className="ui-mdc-booking-table-cell">
                        <div className="ui-mdc-booking-id">
                          {booking.bookingId || booking._id}
                        </div>
                      </td>
                      <td className="ui-mdc-booking-table-cell">
                        <div className="ui-mdc-booking-customer">
                          <div className="ui-mdc-booking-customer-name">
                            {booking.customerName || "N/A"}
                          </div>
                          <div className="ui-mdc-booking-customer-email">
                            {booking.customerEmail || ""}
                          </div>
                          <div className="ui-mdc-booking-customer-phone">
                            {booking.customerPhone || ""}
                          </div>
                        </div>
                      </td>
                      <td className="ui-mdc-booking-table-cell">
                        <div className="ui-mdc-booking-datetime">
                          <div className="ui-mdc-booking-date">
                            {booking.date
                              ? format(new Date(booking.date), "MMM dd, yyyy")
                              : "N/A"}
                          </div>
                          <div className="ui-mdc-booking-time">
                            {formatTimeSlot(booking.timeSlot)}
                          </div>
                        </div>
                      </td>
                      <td className="ui-mdc-booking-table-cell">
                        <div className="ui-mdc-booking-package">
                          <div className="ui-mdc-booking-package-name">
                            {booking.packageName || "N/A"}
                          </div>
                          <div className="ui-mdc-booking-package-details">
                            {booking.park && (
                              <span className="ui-mdc-booking-tag">
                                {booking.park}
                              </span>
                            )}
                            {booking.block && (
                              <span className="ui-mdc-booking-tag">
                                {booking.block}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="ui-mdc-booking-table-cell">
                        <div className="ui-mdc-booking-people">
                          {booking.people || 0} {booking.people === 1 ? "person" : "people"}
                        </div>
                      </td>
                      <td className="ui-mdc-booking-table-cell">
                        <div className="ui-mdc-booking-price">
                          {formatCurrency(booking.totalPrice)}
                        </div>
                      </td>
                      <td className="ui-mdc-booking-table-cell">
                        {getStatusBadge(booking.status)}
                      </td>
                      <td className="ui-mdc-booking-table-cell">
                        {getPaymentStatusBadge(booking.paymentStatus)}
                      </td>
                      <td className="ui-mdc-booking-table-cell">
                        <div className="ui-mdc-booking-actions">
                          <button
                            onClick={() => handleView(booking)}
                            className="ui-mdc-booking-action-button ui-mdc-booking-action-button--view"
                            aria-label="View booking"
                          >
                            <FiEye />
                          </button>
                          {booking.status !== "confirmed" && (
                            <button
                              onClick={() => handleConfirmBooking(booking._id)}
                              className="ui-mdc-booking-action-button ui-mdc-booking-action-button--confirm"
                              aria-label="Confirm booking"
                              title="Confirm booking"
                            >
                              <FiCheckCircle />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(booking._id)}
                            className="ui-mdc-booking-action-button ui-mdc-booking-action-button--delete"
                            aria-label="Delete booking"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                        <button
                            onClick={() => handleAddReview(booking)}
                            className="ui-mdc-package-button ui-mdc-package-button--secondary mt-2"
                            aria-label="Add Review"
                          >
                           Add Review
                          </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View Booking Modal */}
      {isViewModalOpen && viewBooking && (
        <div className="ui-mdc-confirm-modal">
          <div className="ui-mdc-confirm-modal-content ui-mdc-booking-view-modal">
            <div className="ui-mdc-confirm-modal-header">
              <h3 className="ui-mdc-confirm-modal-title">Booking Details</h3>
              <button
                type="button"
                onClick={() => setIsViewModalOpen(false)}
                className="ui-mdc-confirm-modal-close"
                aria-label="Close modal"
              >
                <FiX />
              </button>
            </div>
            <div className="ui-mdc-booking-view-content">
              <div className="ui-mdc-booking-view-section">
                <h4 className="ui-mdc-booking-view-section-title">Booking Information</h4>
                <div className="ui-mdc-booking-view-grid">
                  <div className="ui-mdc-booking-view-item">
                    <label>Booking ID</label>
                    <p>{viewBooking.bookingId || viewBooking._id}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Status</label>
                    <div>{getStatusBadge(viewBooking.status)}</div>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Payment Status</label>
                    <div>{getPaymentStatusBadge(viewBooking.paymentStatus)}</div>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Date</label>
                    <p>
                      {viewBooking.date
                        ? format(new Date(viewBooking.date), "MMM dd, yyyy")
                        : "N/A"}
                    </p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Time Slot</label>
                    <p>{formatTimeSlot(viewBooking.timeSlot)}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>People</label>
                    <p>{viewBooking.people || 0}</p>
                  </div>
                </div>
              </div>

              <div className="ui-mdc-booking-view-section">
                <h4 className="ui-mdc-booking-view-section-title">Customer Information</h4>
                <div className="ui-mdc-booking-view-grid">
                  <div className="ui-mdc-booking-view-item">
                    <label>Name</label>
                    <p>{viewBooking.customerName || "N/A"}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Email</label>
                    <p>{viewBooking.customerEmail || "N/A"}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Phone</label>
                    <p>{viewBooking.customerPhone || "N/A"}</p>
                  </div>
                  {viewBooking.visitorType && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Visitor Type</label>
                      <p className="capitalize">{viewBooking.visitorType}</p>
                    </div>
                  )}
                  {viewBooking.passportNumber && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Passport Number</label>
                      <p>{viewBooking.passportNumber}</p>
                    </div>
                  )}
                  {viewBooking.nicNumber && (
                    <div className="ui-mdc-booking-view-item">
                      <label>NIC Number</label>
                      <p>{viewBooking.nicNumber}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="ui-mdc-booking-view-section">
                <h4 className="ui-mdc-booking-view-section-title">Package Details</h4>
                <div className="ui-mdc-booking-view-grid">
                  <div className="ui-mdc-booking-view-item">
                    <label>Package Name</label>
                    <p>{viewBooking.packageName || "N/A"}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Park</label>
                    <p className="capitalize">{viewBooking.park || "N/A"}</p>
                  </div>
                  {viewBooking.block && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Block</label>
                      <p className="capitalize">{viewBooking.block}</p>
                    </div>
                  )}
                  {viewBooking.jeepType && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Jeep Type</label>
                      <p className="capitalize">{viewBooking.jeepType}</p>
                    </div>
                  )}
                  {viewBooking.guideOption && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Guide Option</label>
                      <p className="capitalize">{viewBooking.guideOption}</p>
                    </div>
                  )}
                  {viewBooking.reservationType && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Reservation Type</label>
                      <p className="capitalize">{viewBooking.reservationType}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="ui-mdc-booking-view-section">
                <h4 className="ui-mdc-booking-view-section-title">Meal Options</h4>
                <div className="ui-mdc-booking-view-grid">
                  <div className="ui-mdc-booking-view-item">
                    <label>Meal Option</label>
                    <p className="capitalize">{viewBooking.mealOption || "N/A"}</p>
                  </div>
                  {viewBooking.vegOption && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Veg Option</label>
                      <p className="capitalize">{viewBooking.vegOption}</p>
                    </div>
                  )}
                  {viewBooking.includeBreakfast && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Breakfast</label>
                      <p>{viewBooking.includeBreakfast ? "Yes" : "No"}</p>
                    </div>
                  )}
                  {viewBooking.includeLunch && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Lunch</label>
                      <p>{viewBooking.includeLunch ? "Yes" : "No"}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="ui-mdc-booking-view-section">
                <h4 className="ui-mdc-booking-view-section-title">Pricing</h4>
                <div className="ui-mdc-booking-view-grid">
                  {viewBooking.ticketPrice > 0 && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Ticket Price</label>
                      <p>{formatCurrency(viewBooking.ticketPrice)}</p>
                    </div>
                  )}
                  {viewBooking.jeepPrice > 0 && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Jeep Price</label>
                      <p>{formatCurrency(viewBooking.jeepPrice)}</p>
                    </div>
                  )}
                  {viewBooking.guidePrice > 0 && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Guide Price</label>
                      <p>{formatCurrency(viewBooking.guidePrice)}</p>
                    </div>
                  )}
                  {viewBooking.mealPrice > 0 && (
                    <div className="ui-mdc-booking-view-item">
                      <label>Meal Price</label>
                      <p>{formatCurrency(viewBooking.mealPrice)}</p>
                    </div>
                  )}
                  <div className="ui-mdc-booking-view-item ui-mdc-booking-view-item--total">
                    <label>Total Price</label>
                    <p className="ui-mdc-booking-total-price">
                      {formatCurrency(viewBooking.totalPrice)}
                    </p>
                  </div>
                </div>
              </div>

              {(viewBooking.pickupLocation || viewBooking.accommodation || viewBooking.hotelWhatsapp) && (
                <div className="ui-mdc-booking-view-section">
                  <h4 className="ui-mdc-booking-view-section-title">Additional Information</h4>
                  <div className="ui-mdc-booking-view-grid">
                    {viewBooking.pickupLocation && (
                      <div className="ui-mdc-booking-view-item">
                        <label>Pickup Location</label>
                        <p>{viewBooking.pickupLocation}</p>
                      </div>
                    )}
                    {viewBooking.accommodation && (
                      <div className="ui-mdc-booking-view-item">
                        <label>Accommodation</label>
                        <p>{viewBooking.accommodation}</p>
                      </div>
                    )}
                    {viewBooking.hotelWhatsapp && (
                      <div className="ui-mdc-booking-view-item">
                        <label>Hotel WhatsApp</label>
                        <p>{viewBooking.hotelWhatsapp}</p>
                      </div>
                    )}
                    {viewBooking.localContact && (
                      <div className="ui-mdc-booking-view-item">
                        <label>Local Contact</label>
                        <p>{viewBooking.localContact}</p>
                      </div>
                    )}
                    {viewBooking.localAccommodation && (
                      <div className="ui-mdc-booking-view-item">
                        <label>Local Accommodation</label>
                        <p>{viewBooking.localAccommodation}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="ui-mdc-booking-view-section">
                <h4 className="ui-mdc-booking-view-section-title">Timestamps</h4>
                <div className="ui-mdc-booking-view-grid">
                  <div className="ui-mdc-booking-view-item">
                    <label>Created At</label>
                    <p>
                      {viewBooking.createdAt
                        ? format(new Date(viewBooking.createdAt), "MMM dd, yyyy HH:mm")
                        : "N/A"}
                    </p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Updated At</label>
                    <p>
                      {viewBooking.updatedAt
                        ? format(new Date(viewBooking.updatedAt), "MMM dd, yyyy HH:mm")
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="ui-mdc-confirm-modal-actions">
              <button
                type="button"
                onClick={() => setIsViewModalOpen(false)}
                className="ui-mdc-confirm-button ui-mdc-confirm-button--secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteConfirmOpen && (
        <div className="ui-mdc-confirm-modal">
          <div className="ui-mdc-confirm-modal-content">
            <div className="ui-mdc-confirm-modal-header">
              <div className="ui-mdc-confirm-modal-icon">
                <FiTrash2 />
              </div>
              <h3 className="ui-mdc-confirm-modal-title">Confirm Booking Deletion</h3>
            </div>
            <div className="ui-mdc-confirm-modal-body">
              <p className="ui-mdc-confirm-modal-message">
                Are you sure you want to delete this booking? This action cannot be undone.
              </p>
            </div>
            <div className="ui-mdc-confirm-modal-actions">
              <button
                type="button"
                onClick={() => {
                  setIsDeleteConfirmOpen(false);
                  setBookingToDelete(null);
                }}
                className="ui-mdc-confirm-button ui-mdc-confirm-button--secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="ui-mdc-confirm-button ui-mdc-confirm-button--danger"
              >
                Delete Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => {
          setIsReviewModalOpen(false);
          setReviewBooking(null);
        }}
        booking={reviewBooking}
      />
    </div>
  );
};

export default BookingsManager;
